import Analytics from 'analytics';
import segmentPlugin from '@analytics/segment';
import { addSentryBreadcrumb } from './sentry';

// Check if we're on the production domain
const isProductionDomain = () => {
  return window.location.hostname === 'trycarto.ai';
};

// Check if analytics should be disabled
const isAnalyticsDisabled = () => {
  return localStorage.getItem('analytics_off') === 'true';
};

// Segment write key - safe to commit (client-side analytics key)
const SEGMENT_WRITE_KEY = 'G86nx00Nj5UcE8OahMGkCxpCvApJNBBq';

// Initialize analytics with Segment only on production domain and when analytics are not disabled
const analytics = (isProductionDomain() && !isAnalyticsDisabled()) ? Analytics({
  app: 'chart-your-course-ai',
  plugins: [
    segmentPlugin({
      writeKey: SEGMENT_WRITE_KEY,
    })
  ]
}) : null;

// Helper function to safely track events
const safeTrack = (eventName: string, properties?: Record<string, any>) => {
  // Don't track if analytics are disabled
  if (isAnalyticsDisabled()) {
    console.log(`[ANALYTICS DISABLED] Would track: ${eventName}`, properties);
    return;
  }

  // Only track if we're on production domain
  if (!isProductionDomain()) {
    console.log(`[DEV] Would track: ${eventName}`, properties);
    return;
  }

  try {
    analytics?.track(eventName, properties);
    
    // Also add as Sentry breadcrumb for error context
    addSentryBreadcrumb(eventName, 'analytics', properties);
  } catch (error) {
    console.log(`Analytics Track: ${eventName}`, properties);
    console.warn('Analytics tracking error:', error);
  }
};

// Helper function to safely track page views
const safePage = (properties?: Record<string, any>) => {
  // Don't track if analytics are disabled
  if (isAnalyticsDisabled()) {
    console.log('[ANALYTICS DISABLED] Would track page view:', properties);
    return;
  }

  // Only track if we're on production domain
  if (!isProductionDomain()) {
    console.log('[DEV] Would track page view:', properties);
    return;
  }

  try {
    analytics?.page(properties);
    
    // Also add as Sentry breadcrumb for error context
    addSentryBreadcrumb('Page View', 'navigation', properties);
  } catch (error) {
    console.log('Analytics Page View:', properties);
    console.warn('Analytics page tracking error:', error);
  }
};

// Track page views
export const trackPageView = (pageName?: string, properties?: Record<string, any>) => {
  safePage({
    name: pageName,
    ...properties
  });
};

// Track when user focuses on email input
export const trackEmailFocus = (formSource: string) => {
  safeTrack('Email Input Focused', {
    form_source: formSource,
    timestamp: new Date().toISOString()
  });
};

// Track when user enters email (on input change)
export const trackEmailEntered = (formSource: string, email?: string) => {
  safeTrack('Email Entered', {
    form_source: formSource,
    has_email: !!email,
    email_length: email?.length || 0,
    timestamp: new Date().toISOString()
  });
};

// Track CTA click (form submission)
export const trackCTAClick = (formSource: string, email: string) => {
  safeTrack('CTA Click - Email Submit', {
    form_source: formSource,
    email: email,
    timestamp: new Date().toISOString()
  });
};

// Track successful email submission
export const trackEmailSubmissionSuccess = (formSource: string, email: string) => {
  safeTrack('Email Submission Success', {
    form_source: formSource,
    email: email,
    timestamp: new Date().toISOString()
  });
};

// Track failed email submission
export const trackEmailSubmissionError = (formSource: string, email: string, error: string) => {
  safeTrack('Email Submission Error', {
    form_source: formSource,
    email: email,
    error: error,
    timestamp: new Date().toISOString()
  });
};

// Identify users (when we have user data)
export const identifyUser = (userId: string, traits?: Record<string, any>) => {
  // Don't identify if analytics are disabled
  if (isAnalyticsDisabled()) {
    console.log(`[ANALYTICS DISABLED] Would identify user: ${userId}`, traits);
    return;
  }

  // Only identify if we're on production domain
  if (!isProductionDomain()) {
    console.log(`[DEV] Would identify user: ${userId}`, traits);
    return;
  }

  try {
    analytics?.identify(userId, {
      ...traits,
      timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.log(`Analytics Identify: ${userId}`, traits);
    console.warn('Analytics identify error:', error);
  }
};

export default analytics; 